import * as React from "react";
import { useCallback, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@progress/kendo-react-buttons";
import { TimePicker } from "@progress/kendo-react-dateinputs";
import { Input } from "@progress/kendo-react-inputs";
import { PopupPropsContext } from "@progress/kendo-react-popup";
import { format } from "date-fns";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import { dateConvert } from "../../../../src/utils/common";
import store from "../../../store";
import { dateConvertUTC } from "../../../utils";
import { setReminderCount } from "../../../actions/notifications";
import { setReminderId, updateCommonGridReminders } from "../../../actions/reminders";
import { label, number } from "../../../config/index";
import { formatTime, parseTimeStringToDate } from "../../../helper/common";
import { planMyDayReminderActions } from "../../TaskSidebar/PlanMyDayActions/planMyDay.helper";
import { handleTaskKeyUpdate } from "../tasks.service";
import "./reminder.scss";
import { addReminder } from "./reminder.service";

/**
 * Default date Picker
 * @returns datePicker
 * @author Shivam Mishra
 */

export const ReminderForm = React.memo((fieldRenderProps = fieldRenderProps) => {
  const [show, setShow] = React.useState(false);
  const { handleAddRem, TaskId, reminderId, anchor, Name, reminderDate, reminderTime, disable, setEditAddRem, fromGrid, setIsSetClicked, setUpdatedValue, setShowReminder } = fieldRenderProps;
  const [reminderMessage, setReminderMessage] = React.useState(Name);
  const { task } = useSelector((state) => state.taskSidebar);
  const { id, UserReminderTime } = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const [minDate] = useState(new Date());
  const formattedTime = formatTime(UserReminderTime, "HH:mm");
  const [selectedDate, setSelectedDate] = useState(reminderDate ? new Date(reminderDate) : new Date());
  const [selectedTime, setselectedTime] = useState(parseTimeStringToDate(reminderTime ? reminderTime : formattedTime));
  const { commonGridReminders } = useSelector((state) => state.reminders);
  const { reminderCount } = useSelector((state) => state.notifications);

  /**
   * returns the updated reminder
   * @param {*} payload
   * @returns {Object} updatedReminder
   * @author Sarthak Arora
   */
  const getUpdatedReminders = (payload) => {
    const { reminderId, reminderMessage, reminderTime, reminderDate } = payload;
    const updatedReminder = commonGridReminders.find((rem) => rem.Id === reminderId);
    if (updatedReminder) {
      updatedReminder.ReminderMessage = reminderMessage;
      updatedReminder.ReminderTime = reminderTime;
      updatedReminder.ReminderDate = reminderDate;
      updatedReminder.Status = new Date(reminderDate) > new Date() ? label.UPCOMING : label.DUE;
    }
    const updatedCommonGridReminders = commonGridReminders.map((reminder) => (reminder.Id === reminderId ? updatedReminder : reminder));

    return updatedCommonGridReminders;
  };

  /**
   * handle time picker
   * @author Sarthak Arora
   */
  const handleTimeChange = (e) => {
    setselectedTime(e.target.value);
  };

  const handleSubmit = useCallback(
    async (e) => {
      const reminderDateObj = new Date(selectedDate);
      const userTimezone = store.getState()?.auth?.user?.timezone || "UTC";
      const localMoment = new Date(
        reminderDateObj.getFullYear(),
        reminderDateObj.getMonth(),
        reminderDateObj.getDate(),
        selectedTime.getHours(),
        selectedTime.getMinutes(),
        0,
        0
      );
      const utcMoment = zonedTimeToUtc(localMoment, userTimezone);
      const payloadReminderTime = formatInTimeZone(utcMoment, "UTC", "HH:mm");
      const payloadReminderDate = dateConvertUTC(utcMoment);

      const payload = {
        userId: id,
        reminderMessage: reminderMessage,
        reminderTime: payloadReminderTime,
        reminderDate: payloadReminderDate,
        entityId: TaskId,
        reminderId: reminderId ? reminderId : null,
      };
      setShowReminder(false);
      handleAddRem && handleAddRem(false);
      if (setEditAddRem) setEditAddRem(false);
      dispatch(setReminderId(null, null));
      if (!reminderId) {
        handleTaskKeyUpdate(false, "reminderCount", task.reminderCount + number.ONE);
      }
      const res = await dispatch(addReminder(payload, false, fromGrid ? true : false));
      if (res) dispatch(updateCommonGridReminders(getUpdatedReminders(payload)));
      planMyDayReminderActions();
      setIsSetClicked && setIsSetClicked(true);
      setUpdatedValue &&
        setUpdatedValue({
          reminderMessage: reminderMessage,
          reminderTime: payloadReminderTime,
          reminderDate: payloadReminderDate,
        });
      dispatch(setReminderCount(reminderCount + number.ONE));
    },
    [reminderMessage, selectedTime, selectedDate, TaskId, reminderId]
  );

  /**
   *  sets the reminderMessage
   *  @author: Sarthak Arora
   */
  const inputEvent = (event) => {
    setReminderMessage(event.target.value);
  };

  /**
   *  array of all the disabled dates (all previous dates)
   *  @author: Sarthak Arora
   */

  const disabledDays = [{ from: new Date(1990, 4, 18), to: new Date() }];

  return (
    <PopupPropsContext.Provider
      value={(props) => ({
        ...props,
        appendTo: anchor.current,
      })}>
      <div className='form-group mt-2'>
        <label>{label.TITLE}</label>
        <textarea id='form-components-reminder-title' className='w-100 form-control' type='text' rows={3} onChange={inputEvent} placeholder={label.TITLE} defaultValue={Name} maxLength={120} />
      </div>

      <DayPicker className='m-0 p-0' mode='single' selected={selectedDate} onSelect={setSelectedDate} disabled={disable || disabledDays} />

      <div className='d-flex justify-content-between align-items-center'>
        <TimePicker steps={{ minute: 5 }} value={selectedTime} className=' ml-2 w-75 mt-2 form-component-input' onChange={handleTimeChange} />

        <Button id='form-components-reminder-set-btn' className='btn btn-primary mt-2' onClick={handleSubmit}>
          {label.SET}
        </Button>
      </div>
    </PopupPropsContext.Provider>
  );
});
